import { Close, Delete, Description, Upload, Visibility, WarningAmber } from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  Input,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { CustomButton } from "src/Components/Common/Button/CustomButton";
import LoadingIcon from "src/Components/Common/LoadingIcon";
import { DIALOG_MAX_WIDTHS } from "src/Components/Common/Modal/ModalWidth";
import useFetch from "src/Components/Common/useFetch";
import CustomToast from "src/Components/CustomToast";
import { API_ENDPOINTS } from "src/Utils/ApiConstants/ApiUrlConstants";
import {
  AttachmentType,
  DocType,
  SummaryStatus,
  VendorDisputeStatusEnumDR,
} from "../../DisputeResolution/DisputedEntries/constants/disputeEntries";
import { useVendorPortal } from "../VendorPortalLayout";

const VpTakeActionDisputedEntries = ({
  selectedRowOfDisputedEntryForModal,
  openTakeActionModal,
  setOpenTakeActionModal,
  listVendorPortalDr,
}) => {
  const [fileInput, setFileInput] = useState<HTMLInputElement | null>(null);
  const [fileInputPod, setFileInputPod] = useState<HTMLInputElement | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [podUploadedFiles, setPodUploadedFiles] = useState([]);
  const [fileInputRaiseDispute, setFileInputRaiseDispute] = useState<HTMLInputElement | null>(null);
  const [uploadedFilesRaiseDispute, setUploadedFilesRaiseDispute] = useState([]);
  const { token } = useVendorPortal();

  // Individual form state variables
  const [vendorRemarks, setVendorRemarks] = useState("");
  const [instamartRemarks, setInstamartRemarks] = useState("");
  const [markAsBooked, setMarkAsBooked] = useState(false);
  const [markCancelled, setMarkCancelled] = useState(false);
  const [gapReason, setGapReason] = useState(selectedRowOfDisputedEntryForModal?.gapReason || "");
  const [dependency, setDependency] = useState(selectedRowOfDisputedEntryForModal?.dependency);
  const [action, setAction] = useState(selectedRowOfDisputedEntryForModal?.action);
  const [resolutionStatus, setResolutionStatus] = useState(selectedRowOfDisputedEntryForModal?.resolutionStatus);
  const [gapReasonOptions, setGapReasonOptions] = useState([selectedRowOfDisputedEntryForModal?.gapReason]);
  const [actionOptions, setActionOptions] = useState([selectedRowOfDisputedEntryForModal?.action]);
  const [attachments, setAttachments] = useState([]);
  const [attachmentsPOD, setAttachmentsPOD] = useState([]);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [poNumber, setPoNumber] = useState(selectedRowOfDisputedEntryForModal?.poNumber || "");
  const [invoiceNumber, setInvoiceNumber] = useState(selectedRowOfDisputedEntryForModal?.invoiceNumber || "");
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const [openRaiseDisputeModal, setOpenRaiseDisputeModal] = useState(false);
  const [vendorRemarksRaiseDispute, setVendorRemarksRaiseDispute] = useState("");
  const [loaderRaiseDispute, setLoaderRaiseDispute] = useState(false);
  const [actionValueIdForClarificationPending, setActionValueIdForClarificationPending] = useState(null);

  // Create a boolean variable for the condition
  const shouldShowFields =
    selectedRowOfDisputedEntryForModal?.docType === DocType.Invoice &&
    (selectedRowOfDisputedEntryForModal?.reconStatus === "Invoice not present in Own Ledger" ||
      selectedRowOfDisputedEntryForModal?.reconStatus === "Invoice - Mismatched Entry - Others");

  // Validation logic for Update button
  const isFormInvalid =
    (shouldShowFields &&
      !markCancelled &&
      (!poNumber.trim() || podUploadedFiles.length + attachmentsPOD.length === 0)) ||
    (selectedRowOfDisputedEntryForModal?.resolutionStatus === SummaryStatus.ClarificationPending &&
      !vendorRemarks.trim());

  const handleClose = () => {
    setOpenTakeActionModal(false);
    // remove this later
    setGapReasonOptions([]);
    setActionOptions([]);
    setUploadedFiles([]);
    setMarkAsBooked(false);
    setMarkCancelled(false);
    setVendorRemarks("");
    setInstamartRemarks("");
    setDependency("");
    setResolutionStatus("");
    setAction("");
    setGapReason("");
    setAttachments([]);
  };
  const handleCloseConfirmationModal = () => {
    setOpenConfirmationModal(false);
    // remove this later
    setGapReasonOptions([]);
  };

  const handleUpdateClick = () => {
    if (markAsBooked || markCancelled) {
      setOpenConfirmationModal(true);
    } else {
      handleSubmit();
    }
  };

  const handleFileUpload = (event) => {
    const selectedFiles = Array.from(event.target.files);
    const totalFiles = uploadedFiles.length + selectedFiles.length;

    if (totalFiles > 5) {
      toast.error(<CustomToast message="You can only upload up to 5 files." />);
      event.target.value = ""; // Clear input so same files can be re-selected
      return;
    }

    setUploadedFiles((prev) => [...selectedFiles, ...prev]);
    event.target.value = "";
  };

  const handlePODFileUpload = (event) => {
    const selectedFiles = Array.from(event.target.files);
    const totalFiles = podUploadedFiles.length + selectedFiles.length;

    if (totalFiles > 5) {
      toast.error(<CustomToast message="You can only upload up to 5 files." />);
      event.target.value = ""; // Clear input so same files can be re-selected
      return;
    }

    setPodUploadedFiles((prev) => [...selectedFiles, ...prev]);
    event.target.value = "";
  };

  const handleRaiseDisputeFileUpload = (event) => {
    const selectedFiles = Array.from(event.target.files);
    const totalFiles = uploadedFilesRaiseDispute.length + selectedFiles.length;

    if (totalFiles > 5) {
      toast.error(<CustomToast message="You can only upload up to 5 files." />);
      event.target.value = ""; // Clear input so same files can be re-selected
      return;
    }

    setUploadedFilesRaiseDispute((prev) => [...selectedFiles, ...prev]);
    event.target.value = "";
  };

  const handleDelete = (index) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDeletePOD = (index) => {
    setPodUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDeleteRaiseDispute = (index) => {
    setUploadedFilesRaiseDispute((prev) => prev.filter((_, i) => i !== index));
  };

  const handleViewAttachment = (file) => {
    useFetch(API_ENDPOINTS.FETCH_VP_DR_ENTRIES_ACTIONS_ATTACHMENTS_VIEW.url, "POST", {
      failureMessage: API_ENDPOINTS.FETCH_VP_DR_ENTRIES_ACTIONS_ATTACHMENTS_VIEW.failureMessage,
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          gapId: selectedRowOfDisputedEntryForModal.gapId,
          attachmentId: file.id,
        },
      },
      thenCallBack: (_res) => {
        const link = document.createElement("a");
        link.href = _res.data.data;
        link.target = "_blank";
        link.referrerPolicy = "no-referrer";
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // window.open(_res.data.attachment, "_blank", "noopener,noreferrer");
      },
    });
  };

  // api integration--------------

  useEffect(() => {
    getVpTakeActionDataActionAttachments(selectedRowOfDisputedEntryForModal?.gapId);
    getVpTakeActionDataRemarks(selectedRowOfDisputedEntryForModal?.gapId);
    if (selectedRowOfDisputedEntryForModal?.resolutionStatus === SummaryStatus.ClarificationPending) {
      getActionValueIdForClarificationPending(
        selectedRowOfDisputedEntryForModal?.actionValueId,
        selectedRowOfDisputedEntryForModal?.gapId
      );
    }
  }, []);

  const getVpTakeActionDataActionAttachments = async (gapId) => {
    await useFetch(API_ENDPOINTS.FETCH_VP_DR_ENTRIES_ACTION_ATTACHMENTS(gapId).url, "GET", {
      failureMessage: API_ENDPOINTS.FETCH_VP_DR_ENTRIES_ACTION_ATTACHMENTS(gapId).failureMessage,
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
      thenCallBack: (response) => {
        const allAttachments = response.data.data || [];

        // Separate attachments based on AttachmentType
        const podAttachments = allAttachments.filter((attachment) => attachment.attachmentType === AttachmentType.POD);
        const otherAttachments = allAttachments.filter(
          (attachment) => attachment.attachmentType === AttachmentType.Others
        );

        setAttachmentsPOD(podAttachments);
        setAttachments(otherAttachments);
      },
      catchCallBack: () => {},
    });
  };

  const getVpTakeActionDataRemarks = async (gapId) => {
    await useFetch(API_ENDPOINTS.FETCH_VP_DR_ENTRIES_REMARK(gapId).url, "GET", {
      failureMessage: API_ENDPOINTS.FETCH_VP_DR_ENTRIES_REMARK(gapId).failureMessage,
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
      thenCallBack: (response) => {
        setVendorRemarks(response.data.data.VendorRemark || "");
        setInstamartRemarks(response.data.data.InstamartRemark || "");
        // setOldValues((prev) => ({ ...prev, vendorRemarks: response.data.VendorRemark || "", instamartRemarks: response.data.InstamartRemark || "" }));
      },
      catchCallBack: () => {},
    });
  };
  const getActionValueIdForClarificationPending = async (id, gapId) => {
    await useFetch(API_ENDPOINTS.FETCH_VP_DR_GET_ACTION_VALUE_ID_FOR_CLARIFICATION_PENDING().url, "GET", {
      failureMessage: API_ENDPOINTS.FETCH_VP_DR_GET_ACTION_VALUE_ID_FOR_CLARIFICATION_PENDING().failureMessage,
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          oldActionValueId: id,
          gapId: gapId,
        },
      },
      thenCallBack: (response) => {
        setActionValueIdForClarificationPending(response.data.data);
      },
      catchCallBack: () => {},
    });
  };

  const handleSubmit = () => {
    updateTakeActionDataAction(selectedRowOfDisputedEntryForModal?.gapId);
  };

  const updateTakeActionDataAction = async (gapId) => {
    setIsLoadingSubmit(true);
    const bodyFormData = new FormData();

    for (const item of uploadedFiles) {
      bodyFormData.append("Others", item);
    }
    // Add POD files with "POD" attachment type
    for (const item of podUploadedFiles) {
      bodyFormData.append("POD", item);
    }
    bodyFormData.append("newRemark", vendorRemarks || "");
    bodyFormData.append("oldGapIdActionValue", selectedRowOfDisputedEntryForModal?.actionValueId || "");
    bodyFormData.append(
      "newGapIdActionValue",
      (uploadedFiles.length || podUploadedFiles.length) > 0
        ? selectedRowOfDisputedEntryForModal?.reviewPendingId
        : markAsBooked
        ? selectedRowOfDisputedEntryForModal?.vendorBookId
        : markCancelled
        ? selectedRowOfDisputedEntryForModal?.vendorCancelId
        : selectedRowOfDisputedEntryForModal?.resolutionStatus === SummaryStatus.ClarificationPending
        ? actionValueIdForClarificationPending
        : ""
    );
    bodyFormData.append("newPoNumber", poNumber || "");
    bodyFormData.append("oldPoNumber", selectedRowOfDisputedEntryForModal?.poNumber || "");
    bodyFormData.append("newInvoiceNumber", invoiceNumber || "");
    bodyFormData.append("oldInvoiceNumber", selectedRowOfDisputedEntryForModal?.invoiceNumber || "");

    await useFetch(API_ENDPOINTS.UPDATE_VP_DR_ENTRIES_ACTION(gapId).url, "POST", {
      failureMessage: API_ENDPOINTS.UPDATE_VP_DR_ENTRIES_ACTION(gapId).failureMessage,
      showSuccessToast: true,
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },

      data: bodyFormData,
      thenCallBack: (_response) => {
        //
        listVendorPortalDr(token);
        handleClose();
        setIsLoadingSubmit(false);
      },
      catchCallBack: () => {
        setIsLoadingSubmit(false);
      },
    });
  };

  const handleCloseRaiseDisputeModal = () => {
    setOpenRaiseDisputeModal(false);
  };

  const handleRaiseDispute = async () => {
    setLoaderRaiseDispute(true);
    const bodyFormData = new FormData();

    for (const item of uploadedFilesRaiseDispute) {
      bodyFormData.append("POD", item);
    }

    bodyFormData.append("issueDescription", vendorRemarksRaiseDispute || "");
    bodyFormData.append("gapId", selectedRowOfDisputedEntryForModal?.gapId || "");

    await useFetch(API_ENDPOINTS.RAISE_DISPUTE_VP_DR_ENTRIES_ACTION.url, "POST", {
      failureMessage: API_ENDPOINTS.RAISE_DISPUTE_VP_DR_ENTRIES_ACTION.failureMessage,
      showSuccessToast: true,
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },

      data: bodyFormData,
      thenCallBack: (_response) => {
        listVendorPortalDr(token);
        setLoaderRaiseDispute(false);
        handleCloseRaiseDisputeModal();
        handleClose();
      },
      catchCallBack: () => {
        setLoaderRaiseDispute(false);
      },
    });
  };

  return (
    <>
      <Dialog open={openTakeActionModal} maxWidth={DIALOG_MAX_WIDTHS.SM} fullWidth>
        <DialogTitle className="dialog-title">
          <div className="heading-m">{""}</div>
          <div className="vertical_center_align">
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          </div>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {shouldShowFields && (
              <>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    className="update-custom-border-radius"
                    label="PO Number"
                    required
                    placeholder="-"
                    variant="outlined"
                    fullWidth
                    value={poNumber}
                    onChange={(e) => setPoNumber(e.target.value)}
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "40px",
                      },
                    }}
                  />
                  <TextField
                    className="update-custom-border-radius"
                    label="Invoice Number"
                    placeholder="-"
                    variant="outlined"
                    fullWidth
                    value={invoiceNumber}
                    onChange={(e) => setInvoiceNumber(e.target.value)}
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "40px",
                      },
                    }}
                  />
                </Box>

                {/* Upload POD Document Section */}
                <Card sx={{ border: "1px solid #d6d6d6" }}>
                  <CardContent sx={{ padding: "16px 24px" }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Box>
                        <Typography className="heading-xs">Upload POD*</Typography>

                        <Typography className="body-s-medium" color="textSecondary">
                          Attach your doc file here (supported formats PDF, JPG, PNG). File size limit 50MB.
                        </Typography>
                      </Box>

                      <>
                        <Input
                          type="file"
                          inputRef={(ref) => setFileInputPod(ref)}
                          sx={{ display: "none" }}
                          onChange={handlePODFileUpload}
                          inputProps={{
                            accept: ".pdf,.jpg,.png,.xlsx,.xls",
                          }}
                        />
                        <CustomButton
                          size="small"
                          colorToken="violet"
                          variant="outlined"
                          startIcon={<Upload />}
                          onClick={() => fileInputPod?.click()}
                          disabled={podUploadedFiles.length >= 5}
                        >
                          Upload
                        </CustomButton>
                      </>
                    </Box>
                  </CardContent>

                  {podUploadedFiles.map((file, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "8px 24px",
                        backgroundColor: "#f2f2f2",
                        borderTop: "1px solid #d6d6d6",
                      }}
                    >
                      <Chip
                        avatar={
                          <Avatar sx={{ backgroundColor: "#541c4c", color: "white" }}>
                            <Description sx={{ fontSize: 14, color: "white" }} />
                          </Avatar>
                        }
                        label={
                          <Box
                            title={file.name}
                            sx={{
                              maxWidth: "250px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {file.name}
                          </Box>
                        }
                        variant="outlined"
                        sx={{
                          maxWidth: "250px",
                          borderColor: "#541c4c",
                          color: "#541c4c",
                          flex: 1,
                        }}
                      />
                      <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
                        <CustomButton
                          size="small"
                          color="error"
                          variant="outlined"
                          startIcon={<Delete />}
                          onClick={() => handleDeletePOD(index)}
                        >
                          Delete
                        </CustomButton>
                      </Box>
                    </Box>
                  ))}

                  {attachmentsPOD?.length > 0 &&
                    attachmentsPOD?.map((file, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "8px 24px",
                          backgroundColor: "#f2f2f2",
                          borderTop: "1px solid #d6d6d6",
                        }}
                      >
                        <Chip
                          avatar={
                            <Avatar sx={{ backgroundColor: "#541c4c", color: "white" }}>
                              <Description sx={{ fontSize: 14, color: "white" }} />
                            </Avatar>
                          }
                          label={
                            <Box
                              title={file.fileName}
                              sx={{
                                maxWidth: "250px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {file.fileName}
                            </Box>
                          }
                          variant="outlined"
                          sx={{
                            maxWidth: "250px",
                            borderColor: "#541c4c",
                            color: "#541c4c",
                            flex: 1, // Allow the chip to take available space
                          }}
                        />
                        <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
                          <CustomButton
                            size="small"
                            colorToken="violet"
                            variant="outlined"
                            startIcon={<Visibility />}
                            onClick={() => handleViewAttachment(file)}
                          >
                            View
                          </CustomButton>
                        </Box>
                      </Box>
                    ))}
                </Card>
              </>
            )}

            {/* Upload Document Section */}
            <Card sx={{ border: "1px solid #d6d6d6" }}>
              <CardContent sx={{ padding: "16px 24px" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Box>
                    <Typography className="heading-xs">Upload Invoice Document</Typography>

                    <Typography className="body-s-medium" color="textSecondary">
                      Attach your doc file here (supported formats PDF, JPG, PNG). File size limit 50MB.
                    </Typography>
                  </Box>

                  <>
                    <Input
                      type="file"
                      inputRef={(ref) => setFileInput(ref)}
                      sx={{ display: "none" }}
                      onChange={handleFileUpload}
                      inputProps={{
                        accept: ".pdf,.jpg,.png,.xlsx,.xls",
                      }}
                    />
                    <CustomButton
                      size="small"
                      colorToken="violet"
                      variant="outlined"
                      startIcon={<Upload />}
                      onClick={() => fileInput?.click()}
                      disabled={uploadedFiles.length >= 5}
                    >
                      Upload
                    </CustomButton>
                  </>
                </Box>
              </CardContent>

              {uploadedFiles.map((file, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "8px 24px",
                    backgroundColor: "#f2f2f2",
                    borderTop: "1px solid #d6d6d6",
                  }}
                >
                  <Chip
                    avatar={
                      <Avatar sx={{ backgroundColor: "#541c4c", color: "white" }}>
                        <Description sx={{ fontSize: 14, color: "white" }} />
                      </Avatar>
                    }
                    label={
                      <Box
                        title={file.name}
                        sx={{
                          maxWidth: "250px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {file.name}
                      </Box>
                    }
                    variant="outlined"
                    sx={{
                      maxWidth: "250px",
                      borderColor: "#541c4c",
                      color: "#541c4c",
                      flex: 1,
                    }}
                  />
                  <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
                    <CustomButton
                      size="small"
                      color="error"
                      variant="outlined"
                      startIcon={<Delete />}
                      onClick={() => handleDelete(index)}
                    >
                      Delete
                    </CustomButton>
                  </Box>
                </Box>
              ))}

              {attachments?.length > 0 &&
                attachments?.map((file, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "8px 24px",
                      backgroundColor: "#f2f2f2",
                      borderTop: "1px solid #d6d6d6",
                    }}
                  >
                    <Chip
                      avatar={
                        <Avatar sx={{ backgroundColor: "#541c4c", color: "white" }}>
                          <Description sx={{ fontSize: 14, color: "white" }} />
                        </Avatar>
                      }
                      label={
                        <Box
                          title={file.fileName}
                          sx={{
                            maxWidth: "250px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {file.fileName}
                        </Box>
                      }
                      variant="outlined"
                      sx={{
                        maxWidth: "250px",
                        borderColor: "#541c4c",
                        color: "#541c4c",
                        flex: 1, // Allow the chip to take available space
                      }}
                    />
                    <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
                      <CustomButton
                        size="small"
                        colorToken="violet"
                        variant="outlined"
                        startIcon={<Visibility />}
                        onClick={() => handleViewAttachment(file)}
                      >
                        View
                      </CustomButton>
                    </Box>
                  </Box>
                ))}
            </Card>

            {/* Action Form Fields */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* Mark as booked and Mark cancelled */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {selectedRowOfDisputedEntryForModal?.vendorBookId && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography>Mark as Booked</Typography>
                    <Switch
                      checked={markAsBooked}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setMarkAsBooked(true);
                          setMarkCancelled(false);
                        } else {
                          setMarkAsBooked(false);
                        }
                      }}
                      color="primary"
                    />
                  </Box>
                )}

                {selectedRowOfDisputedEntryForModal?.vendorCancelId && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography>Mark Cancelled</Typography>
                    <Switch
                      checked={markCancelled}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setMarkCancelled(true);
                          setMarkAsBooked(false);
                        } else {
                          setMarkCancelled(false);
                        }
                      }}
                      color="primary"
                    />
                  </Box>
                )}
              </Box>
              {/* Gap Reason */}
              <FormControl fullWidth size="small" className="update-custom-border-radius" disabled>
                <InputLabel>Gap Reason</InputLabel>
                <Select
                  value={gapReason}
                  label="Gap Reason"
                  onChange={() => {}}
                  sx={{
                    height: "40px",
                  }}
                >
                  {gapReasonOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Action */}
              <FormControl fullWidth size="small" className="update-custom-border-radius" disabled>
                <InputLabel>Action</InputLabel>
                <Select
                  value={action}
                  label="Action"
                  onChange={() => {}}
                  sx={{
                    height: "40px",
                  }}
                >
                  {actionOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Dependency */}
              {/* <FormControl fullWidth size="small"  className="update-custom-border-radius">
                <InputLabel>Dependency</InputLabel>
                <Select
                  value={dependency}
                  label="Dependency"
                  onChange={(e) => setDependency(e.target.value)}
                  sx={{
                    height: '40px'
                  }}
                >
                  {dependencyOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl> */}
              <TextField
                className="update-custom-border-radius"
                label="Dependency"
                placeholder="-"
                variant="outlined"
                fullWidth
                value={dependency}
                onChange={(e) => setDependency(e.target.value)}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "40px",
                  },
                }}
                disabled
              />
              {/* Resolution Status */}
              {/* <FormControl fullWidth size="small"  className="update-custom-border-radius">
                <InputLabel>Resolution Status</InputLabel>
                <Select
                  value={resolutionStatus}
                  label="Resolution Status"
                  onChange={(e) => setResolutionStatus(e.target.value)}
                  sx={{
                    height: '40px'
                  }}
                >
                  {resolutionStatusOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl> */}
              <TextField
                className="update-custom-border-radius"
                label="Resolution Status"
                placeholder="-"
                variant="outlined"
                fullWidth
                value={resolutionStatus}
                onChange={(e) => setResolutionStatus(e.target.value)}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "40px",
                  },
                }}
                disabled
              />

              <TextField
                className="update-custom-border-radius"
                label="Vendor Remarks"
                placeholder="-"
                variant="outlined"
                fullWidth
                value={vendorRemarks}
                onChange={(e) => setVendorRemarks(e.target.value)}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "40px",
                  },
                }}
                required={selectedRowOfDisputedEntryForModal?.resolutionStatus === SummaryStatus.ClarificationPending}
              />
              <TextField
                className="update-custom-border-radius"
                label="Instamart Remarks"
                placeholder="-"
                variant="outlined"
                fullWidth
                value={instamartRemarks}
                onChange={(e) => setInstamartRemarks(e.target.value)}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "40px",
                  },
                }}
                disabled
              />

              {/* Raise Issue Section */}
              {/* <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                <Typography className="body-s-bold" sx={{ flex: 1, color: "rgba(0, 0, 0, 0.60)" }}>
                  Raise issue if you think the current resolution of this entry is incorrect or have any other concern.
                </Typography>
                <CustomButton
                  size="small"
                  color="error"
                  variant="outlined"
                  onClick={() => {
                    // Handle raise issue logic here
                    toast.info(<CustomToast message="Issue raised successfully!" />);
                  }}
                  sx={{
                    minWidth: "120px",
                  }}
                >
                  Raise Issue
                </CustomButton>
              </Box> */}

              {/*  */}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions
          className="dialog-actions"
          sx={{
            justifyContent:
              selectedRowOfDisputedEntryForModal?.resolutionStatus === SummaryStatus.BookingPending ||
              selectedRowOfDisputedEntryForModal?.resolutionStatus === SummaryStatus.ClarificationPending
                ? "space-between"
                : "flex-end",
          }}
        >
          {(selectedRowOfDisputedEntryForModal?.resolutionStatus === SummaryStatus.BookingPending ||
            selectedRowOfDisputedEntryForModal?.resolutionStatus === SummaryStatus.ClarificationPending) && (
            <Tooltip
              title={
                selectedRowOfDisputedEntryForModal?.vendorDisputeStatus === VendorDisputeStatusEnumDR.NO_DISPUTE
                  ? "Raise dispute if you think the current resolution of this entry is incorrect or have any other concern."
                  : "If you have another dispute or your previous issue is still not resolved re-raise issue."
              }
            >
              <CustomButton
                size="medium"
                color="error"
                variant="outlined"
                onClick={() => setOpenRaiseDisputeModal(true)}
              >
                {selectedRowOfDisputedEntryForModal?.vendorDisputeStatus === VendorDisputeStatusEnumDR.NO_DISPUTE
                  ? " Raise Dispute"
                  : "Re-Raise Dispute"}
              </CustomButton>
            </Tooltip>
          )}
          <Box sx={{ display: "flex", gap: 1 }}>
            <CustomButton size="medium" colorToken="violet" variant="outlined" onClick={handleClose}>
              Cancel
            </CustomButton>
            <CustomButton
              size="medium"
              colorToken="violet"
              variant="contained"
              disabled={isFormInvalid || isLoadingSubmit}
              onClick={handleUpdateClick}
            >
              Update
            </CustomButton>
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog open={openConfirmationModal} maxWidth={DIALOG_MAX_WIDTHS.XS} fullWidth>
        <DialogTitle className="dialog-title">
          <div className="heading-m">Are you sure?</div>
          <div className="vertical_center_align">
            <IconButton onClick={handleCloseConfirmationModal}>
              <Close />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Alert severity="info">
            {markAsBooked
              ? "This entry will be moved to subsequently booked. Please make sure you have accounted this entry in your ledger."
              : "This entry will be moved to subsequently cancelled. Please make sure you have cancelled/passed a reversal entry against the disputed entry in your ledger."}
          </Alert>
        </DialogContent>
        <DialogActions className="dialog-actions">
          <CustomButton size="medium" colorToken="violet" variant="outlined" onClick={handleCloseConfirmationModal}>
            Cancel
          </CustomButton>
          <CustomButton
            size="medium"
            colorToken="violet"
            variant="contained"
            //   disabled={uploadedFiles?.length === 0 || isLoadingSubmit}
            //   startIcon={<LoadingIcon loading={isLoadingSubmit} />}
            onClick={handleSubmit}
          >
            Update
          </CustomButton>
        </DialogActions>
      </Dialog>

      {openRaiseDisputeModal && (
        <Dialog open={openRaiseDisputeModal} maxWidth={DIALOG_MAX_WIDTHS.SM} fullWidth>
          <DialogTitle className="dialog-title">
            <div className="heading-m error-dialog-icon">
              <div>
                <WarningAmber color="error" />
              </div>
              Raise Dispute
            </div>
            <div className="vertical_center_align">
              <IconButton
                onClick={() => {
                  handleCloseRaiseDisputeModal();
                }}
              >
                <Close />
              </IconButton>
            </div>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 3 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <Alert severity="info">
                Raise dispute if you think the current resolution of this entry is incorrect or have any other concern.
              </Alert>

              <TextField
                required
                className="update-custom-border-radius"
                label="Vendor Remarks"
                placeholder="-"
                variant="outlined"
                fullWidth
                value={vendorRemarksRaiseDispute}
                onChange={(e) => setVendorRemarksRaiseDispute(e.target.value)}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "40px",
                  },
                }}
              />

              <Card sx={{ border: "1px solid #d6d6d6" }}>
                <CardContent sx={{ padding: "16px 24px" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Box>
                      <Typography className="heading-xs">Upload Supporting Document</Typography>

                      <Typography className="body-s-medium" color="textSecondary">
                        Attach your doc file here (supported formats PDF, JPG, PNG). File size limit 50MB.
                      </Typography>
                    </Box>

                    <>
                      <Input
                        type="file"
                        inputRef={(ref) => setFileInputRaiseDispute(ref)}
                        sx={{ display: "none" }}
                        onChange={handleRaiseDisputeFileUpload}
                        inputProps={{
                          accept: ".pdf,.jpg,.png,.xlsx,.xls",
                        }}
                      />
                      <CustomButton
                        size="small"
                        colorToken="violet"
                        variant="outlined"
                        startIcon={<Upload />}
                        onClick={() => fileInputRaiseDispute?.click()}
                        disabled={uploadedFilesRaiseDispute.length >= 5}
                      >
                        Upload
                      </CustomButton>
                    </>
                  </Box>
                </CardContent>

                {uploadedFilesRaiseDispute.map((file, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "8px 24px",
                      backgroundColor: "#f2f2f2",
                      borderTop: "1px solid #d6d6d6",
                    }}
                  >
                    <Chip
                      avatar={
                        <Avatar sx={{ backgroundColor: "#541c4c", color: "white" }}>
                          <Description sx={{ fontSize: 14, color: "white" }} />
                        </Avatar>
                      }
                      label={
                        <Box
                          title={file.name}
                          sx={{
                            maxWidth: "250px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {file.name}
                        </Box>
                      }
                      variant="outlined"
                      sx={{
                        maxWidth: "250px",
                        borderColor: "#541c4c",
                        color: "#541c4c",
                        flex: 1,
                      }}
                    />
                    <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
                      <CustomButton
                        size="small"
                        color="error"
                        variant="outlined"
                        startIcon={<Delete />}
                        onClick={() => handleDeleteRaiseDispute(index)}
                      >
                        Delete
                      </CustomButton>
                    </Box>
                  </Box>
                ))}
              </Card>
            </Box>
          </DialogContent>
          <DialogActions className="dialog-actions">
            <CustomButton size="medium" color="error" variant="text" onClick={handleCloseRaiseDisputeModal}>
              Cancel
            </CustomButton>
            <CustomButton
              size="medium"
              color="error"
              variant="contained"
              onClick={handleRaiseDispute}
              startIcon={<LoadingIcon loading={loaderRaiseDispute} />}
              disabled={vendorRemarksRaiseDispute.trim() === "" || loaderRaiseDispute}
            >
              Raise Dispute
            </CustomButton>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default VpTakeActionDisputedEntries;
